// schema
import * as ISchema from "@schema";
import { UserStatusEnum } from "@clockwise/schema";

import { RouteProps } from "found";
import { Store } from "redux";
import * as Sentry from "@sentry/browser";

import {
  jwt,
  xsrf,
  analyticsUser,
  orgPrimaryEmail,
  cwUser,
  calDateRange as calDateRangeLocalStorage,
  sudoJwt,
} from "@state/local-storage";
import { IReduxState } from "@state/reducers/root.reducer";
import { getReduxStore } from "@state/redux-store";
import { updateAuthed } from "@state/actions/auth.actions";
import { setMenuDrawerOpen } from "@actions/chrome-extension.actions";
import { alias, identify, track, TrackingEvents } from "@util/analytics.util";
import { getPreCookie } from "@clockwise/web-commons/src/util/pre-cookie.util";
import { getCalDateRange } from "@util/date.util";
import { isValidJson } from "@util/json.util";
import { fromGlobalId } from "@util/graphql.util";
import { PostMessageManager } from "@util/post-message.util";
import {
  getCurrentOrgPrimaryEmailOrBestEffort,
  getCurrentOrg,
  UserWithEmail,
  UserWithOrgs,
  Edges,
} from "@util/org.util";
import { ZonedMoment } from "@util/time-zone.util";
import { getDomainOffEmail } from "@util/email.util";
import { getAuthedRequestInitForFetch } from "@clockwise/web-commons/src/util/fetch.util";
import { logger } from "@util/logger.util";
import { sentryTransport } from "./logger.util";
import { paths } from "@constants/site.constant";
import { windowLocation } from "@util/location.util";
import { API_URL } from "@clockwise/client-commons/src/constants/api";
import { extensionURLForBrowser } from "@clockwise/web-commons/src/util/browser.util";

import { userDeactivated } from "@clockwise/web-commons/src/util/local-storage";
import { isImpersonated, parseJwt } from "@clockwise/client-commons/src/util/jwt";

const sudoDomains = ["getclockwise.com"];
if (process.env.NODE_ENV === "development") {
  sudoDomains.push("wisedevr.com");
} else if (process.env.NODE_ENV === "staging") {
  sudoDomains.push("wisewidgetshq.com");
}

const experimentDomains = ["getclockwise.com", "wisewidgetshq.com", "wisedevr.com"];
const experimentEmailDenyList = ["apple-tester@wisedevr.com"];

////////////////////////
// LOGIN
///////////////////////

interface UserWithTraits extends UserWithEmail {
  id: string;
  status: UserStatusEnum | null;
  givenName: string | null;
  familyName: string | null;
  createdTime: number | null;
}

export function getTraitsFromUser(user?: UserWithTraits) {
  const { id: userId } = (user && fromGlobalId(user.id)) || {};
  const primaryEmail = (user && getCurrentOrgPrimaryEmailOrBestEffort(user)) || undefined;

  return {
    id: userId,
    email: primaryEmail,
    emails: user?.emails,
    status: user?.status,
    givenName: user?.givenName,
    familyName: user?.familyName,
    displayName: user && `${user.givenName} ${user.familyName}`,
    name: user && `${user.givenName} ${user.familyName}`,
    created: user && new ZonedMoment(user.createdTime),
  };
}

interface AuthedViewer {
  user: AuthedUser | null;
}

interface AuthedUser extends UserWithTraits, UserWithOrgs {
  orgs: Edges<Org>;
}

type Org = {
  id: string;
  workingGroups: Edges<{
    id: string;
  }>;
  primaryOrgEmail: string;
};

export function doUserLogin(
  viewer: AuthedViewer,
  userOperation: ISchema.RefreshTokenResultOperationEnum | null,
  orgOperation: ISchema.OrganizationOperationResultEnum | null,
) {
  const user = viewer.user;
  if (!user) {
    logger.error("Expected user in doUserLogin");
    return;
  }
  if (!user.createdTime) {
    logger.error("Expected user.createdTime in doUserLogin");
  }

  // gets the default org, or sets it
  const org = getCurrentOrg(viewer);
  if (!org) {
    logger.error("Expected org in doUserLogin");
    return;
  }
  if (!org.primaryOrgEmail) {
    logger.error("Expected org.primaryOrgEmail in doUserLogin");
    return;
  }
  orgPrimaryEmail.set(org.primaryOrgEmail);
  cwUser.set("true");

  getReduxStore().dispatch(updateAuthed(true, user.createdTime));
  getReduxStore().dispatch(setMenuDrawerOpen(false));

  const { id: userId } = fromGlobalId(user.id);
  const primaryEmail = getCurrentOrgPrimaryEmailOrBestEffort(user);
  analyticsUser.set(`User::${userId}`);

  // sentry
  Sentry.configureScope((scope) => {
    scope.setUser({
      id: user.id,
      email: `${primaryEmail}`,
      username: `${user.givenName} ${user.familyName}`,
    });
  });

  // possibly send chrome extension our authed emails
  PostMessageManager.sendCalendarIds(user.emails);

  // analytics
  alias();
  identify(getTraitsFromUser(user));
  track(TrackingEvents.AUTH.LOGIN, {
    email: primaryEmail,
    emails: user.emails,
    familyName: user.familyName,
    givenName: user.givenName,
    id: userId,
    status: user.status,
    // Add operation property: signup, login
    userCreated: userOperation === ISchema.RefreshTokenResultOperationEnum.Create,
    orgCreated: orgOperation === ISchema.OrganizationOperationResultEnum.Create,
    latestUtms: getPreCookie().latestUtms,
  });

  // google tag manager
  const dataLayer = window && (window as any).dataLayer;
  if (dataLayer) {
    dataLayer.push({ event: "userLoginComplete" });
  }

  if (dataLayer && userOperation === ISchema.RefreshTokenResultOperationEnum.Create) {
    dataLayer.push({ event: "clockwiseUserCreated" });
  }

  if (dataLayer && orgOperation === ISchema.OrganizationOperationResultEnum.Create) {
    dataLayer.push({ event: "clockwiseOrgCreated" });
  }

  userDeactivated.set(false);
}

export function doSudoLogin() {
  // store our new jwt, do a full logout and then set the jwt again
  const tempJwt = jwt.get();
  const tempXsrf = xsrf.get();

  doNormalLogout();

  if (tempJwt && tempXsrf) {
    jwt.set(tempJwt);
    xsrf.set(tempXsrf);

    getReduxStore().dispatch(updateAuthed(true, null));
  } else {
    logger.warn("Failed to complete doSudoLogin because jwt and/or xsrf were unset");
  }
}

////////////////////////
// LOGOUT
///////////////////////

export function doUserLogout() {
  if (isImpersonated(jwt.get())) {
    doSudoLogout();
  } else {
    doNormalLogout();
  }
}

/**
 * This is a special case logout flow used exclusively by internal users who
 * are impersonating another user via sudo
 *
 * Replace impersonated auth tokens with the impersonator's original tokens and
 * reload/redirect the page/iframe
 */
function doSudoLogout() {
  // Restore original jwt, if available
  const originalJwt = sudoJwt.get();
  originalJwt ? jwt.set(originalJwt) : jwt.remove();
  sudoJwt.remove();
  analyticsUser.remove();

  // sentry
  Sentry.configureScope((scope) => scope.clear());

  // app redirect
  if (window.location.pathname.startsWith(paths.logout)) {
    windowLocation.assign("AuthUtilLogoutSwitchUserRedirect", "/chrome.html");
  } else {
    windowLocation.reload("AuthUtilLogoutSwitchUser");
  }
}

/**
 * This is the standard logout flow used by most users
 *
 * Remove auth tokens and redirect if needed
 */
function doNormalLogout() {
  // analytics first
  track(TrackingEvents.AUTH.LOGOUT);

  getReduxStore().dispatch(updateAuthed(false, null));
  jwt.remove();
  xsrf.remove();
  analyticsUser.remove();
  orgPrimaryEmail.remove();

  // sentry
  Sentry.configureScope((scope) => scope.clear());

  // app redirect
  if (window.location.pathname.startsWith(paths.webApp)) {
    windowLocation.assign("AuthUtilLogout", paths.login);
  }
}

////////////////////////
// SESSION HANDLING
///////////////////////

export function getSession(
  store: Store<IReduxState>,
  loadApp: (store: Store<IReduxState>) => void,
) {
  if (window.fetch) {
    const requestInit = getAuthedRequestInitForFetch();
    window
      .fetch(`${API_URL}/session`, requestInit)
      .then((response: any) => {
        // only load app after we have a session backed cookie
        response
          .text()
          .then((text: string) => {
            processSessionResponse(text, store);

            // okay, go ahead and load the app now
            loadApp(store);
          })
          .catch((e: Error) => {
            if (requestInit.headers) {
              sentryTransport.addTag(
                "txnId",
                (requestInit.headers as Headers).get("txnId") || "undefined",
              );
              sentryTransport.addTag(
                "clientId",
                (requestInit.headers as Headers).get("clientId") || "undefined",
              );
            }
            logger.error("session fetch response exception", e);

            loadApp(store);
          });
      })
      .catch((e: Error) => {
        if (requestInit.headers) {
          sentryTransport.addTag(
            "txnId",
            (requestInit.headers as Headers).get("txnId") || "undefined",
          );
          sentryTransport.addTag(
            "clientId",
            (requestInit.headers as Headers).get("clientId") || "undefined",
          );
        }
        logger.error("session fetch exception", e);

        loadApp(store);
      });
  } else {
    logger.error("failed to fetch session, missing window.fetch");

    loadApp(store);
  }
}

export function processSessionResponse(text: string, store: Store<IReduxState>) {
  // store auth state in redux (needed for route processing)
  const parsedText = (isValidJson(text) && JSON.parse(text)) || null;
  const authed = (!!parsedText && parsedText.authed) || false;
  const user = (!!parsedText && (parsedText.user as ISchema.IUser)) || null;
  const userCreatedTime = (user && user.createdTime) || null;
  store.dispatch(updateAuthed(authed, userCreatedTime));

  // analytics and sentry
  if (!!user) {
    const primaryEmail = getCurrentOrgPrimaryEmailOrBestEffort(user);
    if (primaryEmail) {
      orgPrimaryEmail.set(primaryEmail);
    }
    analyticsUser.set(`User::${user.id}`);

    Sentry.configureScope((scope) => {
      scope.setUser({
        id: user.id,
        email: `${primaryEmail}`,
        username: `${user.givenName} ${user.familyName}`,
      });
    });

    // possibly send chrome extension our authed emails
    PostMessageManager.sendCalendarIds(user.emails);

    logger.info("Finishing reauthentication", {
      id: user.id,
      email: primaryEmail || "",
      emails: JSON.stringify(user.emails) || "[]",
    });

    alias();
    identify(getTraitsFromUser(user));
  }
}

//////////////////////////////
// ROUTER ONENTER CALLBACKS
////////////////////////////

// handle access to the app route
export const requireApp = (props: RouteProps) => {
  if (!isAuthed()) {
    return createLoginRedirect(props.location);
  }
  return;
};

export const requireAppWelcomeAmbiguous = (props: RouteProps) => {
  if (!isAuthed()) {
    return createLoginRedirect(props.location, "/welcome-sign-in");
  }
  return;
};

// handle users with no authentication
export const requireClassicAuth = (_props: RouteProps) => {
  if (!isAuthed()) {
    (window as any).location.href = paths.login;
  }
  return "";
};

// if you are authed, get redirected to cal
export const requireNoAuthApp = (_props: RouteProps) => {
  if (isAuthed()) {
    return paths.webApp;
  }
  return;
};

// must be authed and have proper email
export const requireSudo = (_props: RouteProps) => {
  if (!isAuthedSudo()) {
    (window as any).location.href = "/";
  }
  return "";
};

// handle access to the cal route
export const requireCal = (props: RouteProps) => {
  if (!isAuthed()) {
    (window as any).location.href = "/login";
    return;
  } else {
    // ensure there is a calDateRange in the url
    let calDateRangeHint = "";
    const pathParts = props.location.pathname.split("/");
    if (pathParts.length >= 3) {
      calDateRangeHint = pathParts[2];
    }
    const calDateRange = getCalDateRange(calDateRangeHint);

    // update local storage
    if (!calDateRangeLocalStorage.get() || calDateRange !== calDateRangeLocalStorage.get()) {
      calDateRangeLocalStorage.set(calDateRange);
    }

    // if the hint isn't valid, then redirect to a proper url
    if (calDateRange !== calDateRangeHint) {
      let morePath = "";
      if (pathParts.length > 3) {
        morePath = `/${pathParts.slice(3, pathParts.length).join("/")}`;
      }
      return `/cal/${calDateRange}${morePath}`;
    }

    return;
  }
};

export const requireLogout = (_props: RouteProps) => {
  if (window.fetch) {
    if (!isImpersonated(jwt.get())) {
      window.fetch("/clear", { credentials: "include" });
    }
    doUserLogout();
    return "/login";
  }
  logger.error("failed to fetch /clear");
  return "/";
};

export const requireInvite = (_props: RouteProps) => {
  if (extensionURLForBrowser) {
    windowLocation.assign("AuthUtilRequireInvite", extensionURLForBrowser);
  }
  return "/";
};

export const createLoginRedirect = (location: Location, path: string = "/login") => {
  // cleanup
  if (jwt.get() || xsrf.get()) {
    jwt.remove();
    xsrf.remove();
  }

  if (location.pathname !== "/") {
    let nextPath = location.pathname;
    nextPath = nextPath.startsWith("/") ? nextPath.substr(1) : nextPath;
    path += `/${encodeURIComponent(nextPath)}`;
  }
  if (location.search) {
    path += location.search;
  }
  return path;
};

export function isAuthed() {
  const reduxState = getReduxStore().getState();
  return reduxState.auth.authed;
}

export function isAuthedActive() {
  const userJwt = parseJwt(jwt.get());
  return isAuthed() && userJwt?.status === "Active";
}

export function isAuthedSudo() {
  const userJwt = parseJwt(jwt.get());
  return (
    isAuthed() &&
    (userJwt?.emails ?? []).some(
      (email) => sudoDomains.indexOf(getDomainOffEmail(email) || "") !== -1,
    )
  );
}

export function canAccessExperiments() {
  const userJwt = parseJwt(jwt.get());
  // services jwt encoding, impropertly encodes apostrophes. keep settings from blowing up.
  try {
    return (
      isAuthed() &&
      (userJwt?.emails ?? []).some((email) => {
        const domain = getDomainOffEmail(email);
        if (experimentEmailDenyList.indexOf(email) !== -1) {
          return false;
        }
        return (domain && experimentDomains.indexOf(domain) !== -1) || false;
      })
    );
  } catch {
    return false;
  }
}
